export type LandingWorkflow = {
  index: string;
  title: string;
  body: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
    portrait?: boolean;
  };
  proof: string[];
  roadmap: string;
};

export const landingContent = {
  navigation: [
    { label: 'Product', href: '#product' },
    { label: 'Workflows', href: '#workflows' },
    { label: 'Plans', href: '#plans' },
    { label: 'FAQ', href: '#faq' }
  ],
  hero: {
    eyebrow: 'PEOPLE / GREAT HOSPITALITY',
    title: 'Your whole operation, in flow.',
    body: 'Menus, 86s, shift notes, recipes, and training—one precise system for the people who make hospitality happen.'
  },
  outcomes: [
    {
      title: 'Team alignment',
      body: 'Give every role the same operational picture before service begins.'
    },
    {
      title: 'One source of truth',
      body: 'Keep live availability, menu knowledge, and handoffs in one dependable place.'
    },
    {
      title: 'Consistent standards',
      body: 'Turn the way your best people work into repeatable team knowledge.'
    }
  ],
  workflows: [
    {
      index: '01',
      title: 'Open the shift aligned.',
      body: 'Managers set the day in motion with a clear view of what changed, what matters, and where the team needs attention.',
      image: {
        src: '/marketing/shift-desktop-dark.png',
        alt: 'LQD shift dashboard showing service notes and team activity',
        width: 1440,
        height: 1000
      },
      proof: [
        'Shift notes in context',
        'Clear ownership',
        'A cleaner manager handoff'
      ],
      roadmap: 'AI-generated pre-shift briefs.'
    },
    {
      index: '02',
      title: 'Change it once. Everyone knows.',
      body: 'Update an 86, a recipe, or a menu detail at the source so service never depends on a chain of side conversations.',
      image: {
        src: '/marketing/86-mobile-dark.png',
        alt: 'LQD mobile 86 list showing unavailable menu items',
        width: 390,
        height: 844,
        portrait: true
      },
      proof: [
        'Live 86 visibility',
        'Fewer verbal relays',
        'The latest detail at hand'
      ],
      roadmap: 'Photo and PDF menu imports with substitution suggestions.'
    },
    {
      index: '03',
      title: 'Turn house knowledge into team knowledge.',
      body: 'Organize recipes, standards, and training in a library the whole team can actually reach during the work.',
      image: {
        src: '/marketing/library-desktop-dark.png',
        alt: 'LQD knowledge library with organized training and recipe content',
        width: 1440,
        height: 1000
      },
      proof: [
        'Searchable standards',
        'Faster onboarding',
        'Knowledge that stays with the operation'
      ],
      roadmap: 'Permission-aware Ask LQD with bilingual answers.'
    }
  ] satisfies LandingWorkflow[],
  faq: [
    {
      question: 'Who is LQD built for?',
      answer:
        'LQD is built for restaurant and bar operators who need managers and frontline teams working from the same operational truth.'
    },
    {
      question: 'What can my team manage today?',
      answer:
        'The current product brings menus, 86 lists, shift communication, manager notes, recipes, and team knowledge into one workspace.'
    },
    {
      question: 'Can I see plan details before requesting access?',
      answer:
        'Yes. The full pricing page compares Splash, Flow, and Wave using the current plan data.'
    },
    {
      question: 'Are the “Coming next” capabilities available now?',
      answer:
        'No. They are roadmap directions and are deliberately labeled so they are never confused with currently shipped features.'
    },
    {
      question: 'How do I get started?',
      answer:
        'Request access and tell us a little about your operation. The LQD team will follow up directly.'
    }
  ]
} as const;
